import { useEffect, useMemo, useState } from 'react';
import { AppTable, Button, Dialog, Dropdown, InputSearch, InputText, ProgressSpinner, SegmentedControl, StatusTag, TabPanel, TabView, Tooltip } from '@allygpo/ally-ux';
import { getCoreRowModel, getSortedRowModel, useReactTable, type ColumnDef, type Row, type SortingState, type Table } from '@tanstack/react-table';
import { useScenarioContext } from '../../context/ScenarioContext';
import ApiService, { handleApiError } from '../../services/api';
import { usePracticeData } from '../../hooks/usePracticeData';
import FeeScheduleModal from './FeeScheduleModal';
import PayerGroupingUpload from './PayerGroupingUpload';
import { useToast } from '../../context/ToastContext';
import { confirm, ModalHeading, PageHeader, sortableAs, tableView } from '../../components/ui';
import { PayerGroupingReconciliation } from '../../types';


// Styles
import './AccountSettings.css';

// Normalize a group name for name-based matching — must mirror the backend
// _norm_group_key so rename/removal reconciliation lines up with fee-schedule keys.
const normGroup = (s: string): string =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-.,;&()/_]/g, '');

// A group that was removed in this edit session and still has fee-schedule rates.
// A removed group's rates must be reassigned or deleted — never left orphaned.
type RemovedGroupDecision = {
  group: string;
  familyCount: number;
  action: 'reassign' | 'drop';
  target?: string;
};

type ReconcilePlan = {
  // Renamed groups whose rates carry over automatically (shown for transparency).
  renames: { from: string; to: string; familyCount: number }[];
  // Removed groups that still have rates — the user must decide what happens.
  removed: RemovedGroupDecision[];
};

type PayerGroupingResponse = {
  [masterPayorGroup: string]: string[];
};

type EditablePayerRow = {
  rowId: string;
  insurancePayerPrimary: string;
  assignedMasterPayorGroup: string;
};

/** What the Payer assignments grid cells read from `meta.view`. */
interface PayerGridView {
  originalAssignment: Map<string, string>;
  masterPayorOptions: { label: string; value: string }[];
  onAssign: (rowId: string, group: string) => void;
}

const isUnmapped = (row: EditablePayerRow) => !row.assignedMasterPayorGroup || row.assignedMasterPayorGroup === UNMAPPED_GROUP;

const payerColumns: ColumnDef<EditablePayerRow>[] = [
  {
    id: 'payer',
    accessorKey: 'insurancePayerPrimary',
    ...sortableAs('text'),
    size: 520,
    header: 'Insurance payer',
    cell: ({ row: { original: row }, table }) => (
      <div className="payer-row-main flex align-items-center u-gap-2 flex-wrap min-w-0">
        <span className="payer-name font-semibold text-default">{row.insurancePayerPrimary}</span>
        {isUnmapped(row) && <StatusTag variant="warning">Unmapped</StatusTag>}
        {tableView<PayerGridView, EditablePayerRow>(table).originalAssignment.get(row.insurancePayerPrimary) !== row.assignedMasterPayorGroup
          && <span className="setup-changed text-2xs font-semibold text-selected white-space-nowrap">Changed</span>}
      </div>
    ),
  },
  {
    id: 'group',
    size: 240,
    enableSorting: false,
    header: 'Master payer group',
    cell: ({ row: { original: row }, table }) => {
      const { masterPayorOptions, onAssign } = tableView<PayerGridView, EditablePayerRow>(table);
      return (
        <Dropdown
          value={row.assignedMasterPayorGroup}
          options={masterPayorOptions}
          onChange={(event) => onAssign(row.rowId, String(event.value || ''))}
          placeholder="Select master payer group"
          className="w-full payer-row-select"
          aria-label={`Master payer group for ${row.insurancePayerPrimary}`}
        />
      );
    },
  },
];

type MasterGroupRow = {
  group: string;
  count: number;
  hasFeeSchedule: boolean;
  /** Unsaved edit to this group, marked like an edited row in the other Setup tables. */
  change?: 'Renamed' | 'New';
};

/** What the Master payer groups grid cells read from `meta.view`. */
interface MasterGridView {
  coverageLoaded: boolean;
  renamingKey: string | null;
  renameValue: string;
  setRenameValue: (value: string) => void;
  startRename: (group: string) => void;
  cancelRename: () => void;
  rename: (group: string) => void;
  remove: (group: string) => void;
}

const isRenaming = (row: Row<MasterGroupRow>, table: Table<MasterGroupRow>) =>
  tableView<MasterGridView, MasterGroupRow>(table).renamingKey === row.original.group;

// While a group is renamed, its name cell spans the row (the other cells are hidden).
const hiddenWhileRenaming = { getColSpan: (row: Row<MasterGroupRow>, table: Table<MasterGroupRow>) => (isRenaming(row, table) ? 0 : 1) };

const masterColumns: ColumnDef<MasterGroupRow>[] = [
  {
    id: 'name',
    accessorKey: 'group',
    ...sortableAs('text'),
    header: 'Master payer group',
    meta: { getColSpan: (row, table) => (isRenaming(row, table) ? 2 : 1) },
    cell: ({ row: { original: row }, table }) => {
      const view = tableView<MasterGridView, MasterGroupRow>(table);
      if (view.renamingKey !== row.group) {
        return (
          <div className="payer-row-main flex align-items-center u-gap-2 flex-wrap min-w-0">
            <span
              className="master-payor-name font-semibold flex align-items-center u-gap-2"
              data-pr-tooltip={row.count === 0 ? 'No insurance payers assigned' : `${row.count} insurance payer${row.count !== 1 ? 's' : ''} assigned to this group`}
              data-pr-position="top"
            >
              {row.group}
            </span>
            {row.change && <span className="setup-changed text-2xs font-semibold text-selected white-space-nowrap">{row.change}</span>}
            {view.coverageLoaded && (
              <StatusTag
                variant={row.hasFeeSchedule ? 'info' : 'warning'}
                className="master-payor-fs"
                data-pr-tooltip={row.hasFeeSchedule
                  ? 'A fee schedule rate exists for this group'
                  : 'No fee schedule rate for this group — reimbursement falls back to Medicare'}
                data-pr-position="top"
              >
                {row.hasFeeSchedule ? 'Custom rates' : 'Medicare fallback'}
              </StatusTag>
            )}
          </div>
        );
      }
      return (
        <div className="master-payor-rename flex u-gap-2 align-items-center w-full">
          <InputText
            value={view.renameValue}
            onChange={(e) => view.setRenameValue(e.target.value)}
            className="w-full"
            maxLength={50}
            autoFocus
            aria-label={`New name for ${row.group}`}
            onKeyDown={(e) => { if (e.key === 'Enter') view.rename(row.group); }}
          />
          <Button variant="primary-text" aria-label={`Save new name for ${row.group}`} onClick={() => view.rename(row.group)}><i className="pi pi-check" aria-hidden="true" /></Button>
          <Button variant="secondary-text" aria-label="Cancel rename" onClick={view.cancelRename}><i className="pi pi-times" aria-hidden="true" /></Button>
        </div>
      );
    },
  },
  {
    id: 'actions',
    size: 96,
    enableSorting: false,
    header: '',
    meta: hiddenWhileRenaming,
    cell: ({ row: { original: row }, table }) => {
      if (row.group === UNMAPPED_GROUP) return null;
      const view = tableView<MasterGridView, MasterGroupRow>(table);
      return (
        <div className="flex justify-content-end u-gap-1">
          <Button variant="secondary-text" aria-label={`Rename ${row.group}`} onClick={() => view.startRename(row.group)}><i className="pi pi-pencil" aria-hidden="true" /></Button>
          <Button variant="secondary-text" aria-label={`Delete ${row.group}`} onClick={() => view.remove(row.group)}><i className="pi pi-trash" aria-hidden="true" /></Button>
        </div>
      );
    },
  },
];

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const extractGroupedPayerMap = (payload: unknown): PayerGroupingResponse => {
  if (!isRecord(payload)) return {};

  const source = isRecord(payload.data) ? payload.data : payload;
  const grouped: PayerGroupingResponse = {};

  Object.keys(source).forEach((key) => {
    const maybeArray = source[key];
    if (!Array.isArray(maybeArray)) return;

    const names: string[] = maybeArray
      .filter((item) => typeof item === 'string')
      .map((item) => String(item));

    grouped[key] = names;
  });

  return grouped;
};

const extractMasterPayors = (payload: unknown, groupedMap: PayerGroupingResponse): string[] => {
  const groupedKeys = Object.keys(groupedMap);

  if (isRecord(payload)) {
    if (Array.isArray(payload.MasterPayors)) {
      const fromResponse = payload.MasterPayors.map((value) => String(value));
      return Array.from(new Set<string>([...fromResponse, ...groupedKeys]));
    }
    if (isRecord(payload.data) && Array.isArray(payload.data.MasterPayors)) {
      const fromResponse = payload.data.MasterPayors.map((value) => String(value));
      return Array.from(new Set<string>([...fromResponse, ...groupedKeys]));
    }
  }

  return groupedKeys;
};

const flattenPayerGroupings = (
  payload: PayerGroupingResponse
): EditablePayerRow[] => {
  const rows: EditablePayerRow[] = [];

  for (const masterPayorGroup in payload) {
    if (!Object.prototype.hasOwnProperty.call(payload, masterPayorGroup)) {
      continue;
    }

    const payers = payload[masterPayorGroup] || [];
    for (let index = 0; index < payers.length; index += 1) {
      const name = payers[index];
      rows.push({
        rowId: masterPayorGroup + '::' + String(index) + '::' + name,
        insurancePayerPrimary: name,
        assignedMasterPayorGroup: masterPayorGroup,
      });
    }
  }

  return rows.sort((a, b) => a.insurancePayerPrimary.localeCompare(b.insurancePayerPrimary));
};


const UNMAPPED_GROUP = 'Other/Unmapped';
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const SetupChevron = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>;

const AccountSettings: React.FC = () => {
  const { state } = useScenarioContext();
  const { isLoading: isLoadingPractices, isDemoMode } = usePracticeData();
  const toast = useToast();

  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string>('');

  const [showPayerGroupingModal, setShowPayerGroupingModal] = useState(false);
  const [showFeeScheduleModal, setShowFeeScheduleModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [payerRows, setPayerRows] = useState<EditablePayerRow[]>([]);
  const [originalPayerRows, setOriginalPayerRows] = useState<EditablePayerRow[]>([]);
  const [masterPayorGroups, setMasterPayorGroups] = useState<string[]>([]);
  const [originalMasterPayorGroups, setOriginalMasterPayorGroups] = useState<string[]>([]);
  const [isLoadingPayerGroupings, setIsLoadingPayerGroupings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [payerGroupingError, setPayerGroupingError] = useState<string | null>(null);

  const [newMasterPayorName, setNewMasterPayorName] = useState('');
  const [payerSearch, setPayerSearch] = useState('');
  const [payerFilter, setPayerFilter] = useState<'all' | 'unmapped'>('all');
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [uploadActions, setUploadActions] = useState<{ onDiscard: () => void; onApply: () => void; canApply: boolean; onUploadDifferent: () => void } | null>(null);

  // Tracks current group name -> its original (pre-edit) name so a rename can carry
  // its fee-schedule rates over instead of orphaning them. Only renamed groups appear.
  const [groupOrigin, setGroupOrigin] = useState<Record<string, string>>({});

  // Normalized master-group name -> number of product families that have a fee-schedule
  // rate for it. Powers the inline "fee schedule exists?" indicator on the Master Payers
  // tab and the save-time reconciliation check. Loaded alongside the payer groupings.
  const [ratesByGroupNorm, setRatesByGroupNorm] = useState<Map<string, number>>(new Map());
  // True only when the fee-schedule coverage fetch succeeded. If it failed we can't tell
  // which rates a group rename/removal would strand, so we refuse to save (see handleSave).
  const [coverageLoaded, setCoverageLoaded] = useState(false);
  const [isLoadingCoverage, setIsLoadingCoverage] = useState(false);

  // Reconciliation dialog shown on save when a rename/removal would affect fee schedules.
  const [reconcilePlan, setReconcilePlan] = useState<ReconcilePlan | null>(null);
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);

  useEffect(() => {
    if (isLoadingPractices) return;
    setSelectedAccountNumber(state.generatorState?.form?.logged_in_user ?? '');
  }, [isLoadingPractices, state.generatorState?.form?.logged_in_user]);


  const loadPayerGroupings = async () => {
    if (!selectedAccountNumber) {
      setPayerRows([]);
      setMasterPayorGroups([]);
      setPayerGroupingError('No active account selected. Please select an organization first.');
      return;
    }

    setIsLoadingPayerGroupings(true);
    setPayerGroupingError(null);

    try {
      const response = await ApiService.getPayerGroupings(selectedAccountNumber);
      const normalizedResponse = response as unknown;
      const groupedMap = extractGroupedPayerMap(normalizedResponse);
      const rows = flattenPayerGroupings(groupedMap);
      const groups = extractMasterPayors(normalizedResponse, groupedMap);

      setPayerRows(rows);
      setOriginalPayerRows(rows);
      setMasterPayorGroups(groups);
      setOriginalMasterPayorGroups(groups);
      setGroupOrigin({});
    } catch (error) {
      setPayerRows([]);
      setMasterPayorGroups([]);
      setPayerGroupingError(handleApiError(error));
    } finally {
      setIsLoadingPayerGroupings(false);
    }

    // Best-effort: which master groups actually have fee-schedule rates. Drives the
    // inline indicator + save-time reconciliation. Never blocks the groupings view.
    setIsLoadingCoverage(true);
    try {
      const health = await ApiService.getFeeScheduleCoverage(selectedAccountNumber);
      const map = new Map<string, number>();
      for (const f of health.families) {
        const seen = new Set<string>();
        f.mapped_groups.forEach((g) => seen.add(normGroup(g)));
        f.orphaned_rates.forEach((g) => seen.add(normGroup(g)));
        seen.forEach((n) => map.set(n, (map.get(n) ?? 0) + 1));
      }
      setRatesByGroupNorm(map);
      setCoverageLoaded(true);
    } catch {
      setRatesByGroupNorm(new Map());
      setCoverageLoaded(false);
    } finally {
      setIsLoadingCoverage(false);
    }
  };

  useEffect(() => {
    // The Setup tiles summarize the saved groupings and fee-schedule coverage, so load
    // them as soon as a practice is selected, not only when a modal opens.
    if (!selectedAccountNumber || showPayerGroupingModal || showFeeScheduleModal) return;
    loadPayerGroupings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountNumber]);

  useEffect(() => {
    // Load groupings when EITHER modal opens: the Fee Schedules editor needs
    // masterPayorGroups (+ coverage counts) too, otherwise it opens with an empty
    // master-group list and can't surface every payer's rate row.
    if ((!showPayerGroupingModal && !showFeeScheduleModal) || !selectedAccountNumber) return;
    loadPayerGroupings();
  // Reloads are driven by the modals opening and the account, not by the loader's identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPayerGroupingModal, showFeeScheduleModal, selectedAccountNumber]);

  const masterPayorOptions = useMemo(() => {
    // Sort masterPayorGroups alphabetically (case-insensitive)
    const sortedGroups = [...masterPayorGroups].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return sortedGroups.map((value) => ({ label: value, value }));
  }, [masterPayorGroups]);

  const hasAssignmentChanges = useMemo(() => {
    if (payerRows.length !== originalPayerRows.length) return true;
    const originalMap = new Map(originalPayerRows.map((r) => [r.insurancePayerPrimary, r.assignedMasterPayorGroup]));
    return payerRows.some((r) => originalMap.get(r.insurancePayerPrimary) !== r.assignedMasterPayorGroup);
  }, [payerRows, originalPayerRows]);

  const hasMasterPayorChanges = useMemo(() => {
    if (masterPayorGroups.length !== originalMasterPayorGroups.length) return true;
    for (let i = 0; i < masterPayorGroups.length; i += 1) {
      if (masterPayorGroups[i] !== originalMasterPayorGroups[i]) return true;
    }
    return false;
  }, [masterPayorGroups, originalMasterPayorGroups]);

  const payerCountByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of payerRows) {
      counts[row.assignedMasterPayorGroup] = (counts[row.assignedMasterPayorGroup] ?? 0) + 1;
    }
    return counts;
  }, [payerRows]);

  // Setup tile summaries, from the saved state so unsaved edits in an open modal don't move them.
  const setupSummary = useMemo(() => {
    // Other/Unmapped counts as a group here; it just can't carry rates of its own.
    const groups = originalMasterPayorGroups;
    const unmapped = originalPayerRows.filter((row) => !row.assignedMasterPayorGroup || row.assignedMasterPayorGroup === UNMAPPED_GROUP).length;
    // Medicare is the fallback rate itself, so neither it nor Other/Unmapped is a group that could have custom rates.
    const rateGroups = groups.filter((group) => group !== UNMAPPED_GROUP && normGroup(group) !== normGroup('Medicare'));
    const withRates = rateGroups.filter((group) => (ratesByGroupNorm.get(normGroup(group)) ?? 0) > 0).length;
    return { payers: originalPayerRows.length, groups: groups.length, unmapped, rateGroups: rateGroups.length, withRates };
  }, [originalPayerRows, originalMasterPayorGroups, ratesByGroupNorm]);

  const originalAssignment = useMemo(() => new Map(originalPayerRows.map((r) => [r.insurancePayerPrimary, r.assignedMasterPayorGroup])), [originalPayerRows]);
  const changedAssignmentCount = useMemo(
    () => payerRows.filter((r) => originalAssignment.get(r.insurancePayerPrimary) !== r.assignedMasterPayorGroup).length,
    [payerRows, originalAssignment]
  );
  // Master-group edits: renames (tracked in groupOrigin) plus groups added or removed outright.
  const changedGroupCount = useMemo(() => {
    const original = new Set(originalMasterPayorGroups);
    const current = new Set(masterPayorGroups);
    const renamedTo = new Set(Object.keys(groupOrigin).filter((g) => original.has(groupOrigin[g])));
    const renamedFrom = new Set(Array.from(renamedTo).map((g) => groupOrigin[g]));
    const added = masterPayorGroups.filter((g) => !original.has(g) && !renamedTo.has(g)).length;
    const removed = originalMasterPayorGroups.filter((g) => !current.has(g) && !renamedFrom.has(g)).length;
    return added + removed + renamedTo.size;
  }, [masterPayorGroups, originalMasterPayorGroups, groupOrigin]);
  const draftUnmappedCount = useMemo(() => payerRows.filter((r) => !r.assignedMasterPayorGroup || r.assignedMasterPayorGroup === UNMAPPED_GROUP).length, [payerRows]);
  const visiblePayerRows = useMemo(() => {
    const q = payerSearch.trim().toLowerCase();
    return payerRows.filter((r) =>
      (!q || r.insurancePayerPrimary.toLowerCase().includes(q)) &&
      (payerFilter === 'all' || !r.assignedMasterPayorGroup || r.assignedMasterPayorGroup === UNMAPPED_GROUP));
  }, [payerRows, payerSearch, payerFilter]);
  const [payerSorting, setPayerSorting] = useState<SortingState>([{ id: 'payer', desc: false }]);
  const payerTable = useReactTable({
    data: visiblePayerRows,
    columns: payerColumns,
    meta: { view: { originalAssignment, masterPayorOptions, onAssign: (rowId, group) => handleMasterPayorChange(rowId, group) } satisfies PayerGridView },
    state: { sorting: payerSorting },
    onSortingChange: setPayerSorting,
    enableSortingRemoval: false,
    getRowId: (row) => row.rowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const masterRows = useMemo<MasterGroupRow[]>(() => {
    const original = new Set(originalMasterPayorGroups);
    return masterPayorGroups.map((group) => {
      // A group has a fee schedule if a rate exists for it, or for the
      // original name it was renamed from (that rate carries over on save).
      const origin = groupOrigin[group];
      const renamed = !!origin && origin !== group && original.has(origin);
      return {
        group,
        count: payerCountByGroup[group] ?? 0,
        hasFeeSchedule: ratesByGroupNorm.has(normGroup(group)) || (!!origin && ratesByGroupNorm.has(normGroup(origin))),
        change: renamed ? 'Renamed' : !original.has(group) ? 'New' : undefined,
      };
    });
  }, [masterPayorGroups, originalMasterPayorGroups, payerCountByGroup, groupOrigin, ratesByGroupNorm]);
  const [masterSorting, setMasterSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const masterTable = useReactTable({
    data: masterRows,
    columns: masterColumns,
    meta: {
      view: {
        coverageLoaded,
        renamingKey,
        renameValue,
        setRenameValue,
        startRename: (group) => { setRenamingKey(group); setRenameValue(group); },
        cancelRename: () => setRenamingKey(null),
        rename: (group) => handleRenameMasterPayor(group),
        remove: (group) => handleDeleteMasterPayor(group),
      } satisfies MasterGridView,
    },
    state: { sorting: masterSorting },
    onSortingChange: setMasterSorting,
    enableSortingRemoval: false,
    getRowId: (row) => row.group,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const tabChangeCount = activeTab === 0 ? changedAssignmentCount : activeTab === 1 ? changedGroupCount : 0;

  const confirmDiscardTabChanges = () => confirm({
    header: 'Discard changes',
    message: 'Discard your unsaved changes on this tab?',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Discard',
    rejectLabel: 'Stay',
    danger: true,
    accept: discardTabChanges,
  });

  const discardTabChanges = () => {
    if (activeTab === 0) {
      setPayerRows(originalPayerRows);
    } else {
      setMasterPayorGroups(originalMasterPayorGroups);
      setPayerRows(originalPayerRows);
      setGroupOrigin({});
    }
    setRenamingKey(null);
  };

  const activeTabHasChanges = activeTab === 0 ? hasAssignmentChanges : activeTab === 1 ? hasMasterPayorChanges : uploadActions !== null;
  const hasAnyChanges = hasAssignmentChanges || hasMasterPayorChanges || uploadActions !== null;

  // Only "Payer Assignments" is usable until the account has payer plans loaded
  const plansLoaded = payerRows.length > 0;

  useEffect(() => {
    if (!plansLoaded) setActiveTab((tab) => (tab !== 0 ? 0 : tab));
  }, [plansLoaded]);

  const doClosePayerGroupingModal = () => {
    setShowPayerGroupingModal(false);
    setTimeout(() => setActiveTab(0), 300);
    setRenamingKey(null);
    setNewMasterPayorName('');
    setPayerSearch('');
    setPayerFilter('all');
    setUploadActions(null);
    setGroupOrigin({});
  };

  const handlePayerGroupingModalHide = () => {
    if (hasAnyChanges) {
      confirm({
        message: 'You have unsaved changes. Discard them and close?',
        header: 'Unsaved changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard & Close',
        rejectLabel: 'Stay',
        danger: true,
        accept: doClosePayerGroupingModal,
      });
      return;
    }
    doClosePayerGroupingModal();
  };

  const handleTabChange = (nextIndex: number) => {
    if (nextIndex === activeTab) return;
    if (activeTabHasChanges) {
      confirm({
        message: 'You have unsaved changes on this tab. Discard them?',
        header: 'Unsaved changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Stay',
        danger: true,
        accept: () => {
          if (activeTab === 0) {
            setPayerRows(originalPayerRows);
          } else {
            setMasterPayorGroups(originalMasterPayorGroups);
            setPayerRows(originalPayerRows);
            setGroupOrigin({});
          }
          setRenamingKey(null);
          setActiveTab(nextIndex);
        },
      });
      return;
    }
    setActiveTab(nextIndex);
  };

  const buildGrouped = (): Record<string, string[]> => {
    const grouped: Record<string, string[]> = {};
    for (const g of masterPayorGroups) grouped[g] = [];
    for (const row of payerRows) {
      const group = row.assignedMasterPayorGroup;
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(row.insurancePayerPrimary);
    }
    return grouped;
  };

  const persistGroupings = async (reconciliation?: PayerGroupingReconciliation) => {
    setIsSaving(true);
    try {
      await ApiService.savePayerGroupings(selectedAccountNumber, buildGrouped(), reconciliation);
      await loadPayerGroupings();
      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: 'Payer groupings saved successfully.',
        life: 3000,
      });
    } catch (error) {
      console.error('Failed to save payer groupings:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Save Failed',
        detail: handleApiError(error),
        life: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    // Detect group renames / removals that would strand fee-schedule rates keyed to
    // the old name. Renames are tracked live (groupOrigin); removals are whatever
    // original group is no longer present and wasn't renamed.
    const currentNorms = new Set(masterPayorGroups.map(normGroup));
    const originalNorms = new Set(originalMasterPayorGroups.map(normGroup));

    const renameCandidates: { from: string; to: string }[] = [];
    const renamedOriginNorms = new Set<string>();
    for (const g of masterPayorGroups) {
      const origin = groupOrigin[g];
      if (origin && normGroup(origin) !== normGroup(g) && originalNorms.has(normGroup(origin))) {
        renameCandidates.push({ from: origin, to: g });
        renamedOriginNorms.add(normGroup(origin));
      }
    }
    const removedGroups = originalMasterPayorGroups.filter(
      (g) => !currentNorms.has(normGroup(g)) && !renamedOriginNorms.has(normGroup(g))
    );

    // No structural change -> save straight through (unchanged behaviour).
    if (renameCandidates.length === 0 && removedGroups.length === 0) {
      await persistGroupings();
      return;
    }

    // A group was renamed/removed, but we could not load the fee-schedule coverage that
    // tells us which rates would be stranded. Rather than save blindly (and silently
    // orphan a rate), refuse and ask the user to retry once coverage can load.
    if (!coverageLoaded) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Could not verify fee schedules',
        detail: 'We could not check which fee-schedule rates your group changes affect. Please close and reopen this dialog, then try again.',
        life: 5000,
      });
      return;
    }

    // Only groups that actually carry fee-schedule rates need reconciliation. Reuse the
    // presence map already loaded with the groupings (no extra round-trip).
    const renames = renameCandidates
      .filter((r) => ratesByGroupNorm.has(normGroup(r.from)))
      .map((r) => ({ from: r.from, to: r.to, familyCount: ratesByGroupNorm.get(normGroup(r.from)) ?? 0 }));

    const removed: RemovedGroupDecision[] = removedGroups
      .filter((g) => ratesByGroupNorm.has(normGroup(g)))
      .map((g) => ({
        group: g,
        familyCount: ratesByGroupNorm.get(normGroup(g)) ?? 0,
        // Default to reassign (with no target yet) so Save stays disabled until the user
        // consciously picks reassign-to-a-group or delete — never a silent drop.
        action: 'reassign' as const,
      }));

    if (renames.length === 0 && removed.length === 0) {
      await persistGroupings();
      return;
    }

    setReconcilePlan({ renames, removed });
    setShowReconcileDialog(true);
  };

  const updateRemovedDecision = (group: string, patch: Partial<RemovedGroupDecision>) => {
    setReconcilePlan((prev) =>
      prev
        ? { ...prev, removed: prev.removed.map((d) => (d.group === group ? { ...d, ...patch } : d)) }
        : prev
    );
  };

  const reconcileValid = useMemo(() => {
    if (!reconcilePlan) return false;
    return reconcilePlan.removed.every((d) => d.action !== 'reassign' || !!d.target);
  }, [reconcilePlan]);

  const applyReconciliation = async () => {
    if (!reconcilePlan) return;
    const renames: Record<string, string> = {};
    const dropGroups: string[] = [];
    for (const r of reconcilePlan.renames) renames[r.from] = r.to;
    for (const d of reconcilePlan.removed) {
      if (d.action === 'reassign' && d.target) renames[d.group] = d.target;
      else if (d.action === 'drop') dropGroups.push(d.group);
    }
    const reconciliation: PayerGroupingReconciliation = {};
    if (Object.keys(renames).length) reconciliation.renames = renames;
    if (dropGroups.length) reconciliation.drop_groups = dropGroups;

    setShowReconcileDialog(false);
    setReconcilePlan(null);
    await persistGroupings(Object.keys(reconciliation).length ? reconciliation : undefined);
  };

  const handleAddMasterPayor = () => {
    const trimmed = newMasterPayorName.trim();
    if (!trimmed) return;
    // Match by normGroup (not just lowercase) so spacing/punctuation variants like
    // "Managed Care" vs "ManagedCare" can't create two groups that collide downstream.
    if (masterPayorGroups.some((g) => normGroup(g) === normGroup(trimmed))) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Already exists',
        detail: `"${trimmed}" is already a master payer group.`,
        life: 3000,
      });
      return;
    }
    setMasterPayorGroups((prev) => [...prev, trimmed]);
    setNewMasterPayorName('');
  };

  const handleRenameMasterPayor = (oldName: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === oldName) {
      setRenamingKey(null);
      return;
    }
    // Same normGroup-based uniqueness as add: block renaming into a name that already
    // exists OR that collides with one under the system's matching normalization.
    if (masterPayorGroups.some((g) => normGroup(g) === normGroup(trimmed) && g !== oldName)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Already exists',
        detail: `"${trimmed}" is already a master payer group.`,
        life: 3000,
      });
      setRenamingKey(null);
      return;
    }
    setMasterPayorGroups((prev) => prev.map((g) => (g === oldName ? trimmed : g)));
    setPayerRows((prev) =>
      prev.map((row) =>
        row.assignedMasterPayorGroup === oldName
          ? { ...row, assignedMasterPayorGroup: trimmed }
          : row
      )
    );
    // Preserve the original (pre-edit) name across chained renames so its
    // fee-schedule rates can be carried over on save.
    setGroupOrigin((prev) => {
      const next = { ...prev };
      const origin = next[oldName] ?? oldName;
      delete next[oldName];
      if (normGroup(origin) !== normGroup(trimmed)) {
        next[trimmed] = origin;
      }
      return next;
    });
    setRenamingKey(null);
  };

  const handleDeleteMasterPayor = (name: string) => {
    const affectedCount = payerRows.filter((row) => row.assignedMasterPayorGroup === name).length;
    confirm({
      message: affectedCount > 0
        ? `"${name}" has ${affectedCount} payer(s) assigned. They will be moved to "Other/Unmapped". Continue?`
        : `Delete "${name}"? This master payer has no payers assigned.`,
      header: 'Delete master payer group',
      icon: 'pi pi-exclamation-triangle',
      danger: true,
      accept: () => {
        const fallback = 'Other/Unmapped';
        if (masterPayorGroups.indexOf(fallback) === -1) {
          setMasterPayorGroups((prev) => [...prev.filter((g) => g !== name), fallback]);
        } else {
          setMasterPayorGroups((prev) => prev.filter((g) => g !== name));
        }
        setPayerRows((prev) =>
          prev.map((row) =>
            row.assignedMasterPayorGroup === name
              ? { ...row, assignedMasterPayorGroup: fallback }
              : row
          )
        );
        setGroupOrigin((prev) => {
          if (!(name in prev)) return prev;
          const next = { ...prev };
          delete next[name];
          return next;
        });
      },
    });
  };

  const handleMasterPayorChange = (rowId: string, nextMasterPayorGroup: string) => {
    setPayerRows((previousRows) => {
      return previousRows.map((row) => {
        if (row.rowId !== rowId) return row;
        return {
          ...row,
          assignedMasterPayorGroup: nextMasterPayorGroup,
        };
      });
    });
  };

  return (
    <div className="account-settings-container flex flex-column u-gap-4">
      <PageHeader title="Setup" />
      <div className="setup-tiles flex flex-wrap u-gap-4">
        <button type="button" className="setup-tile flex-1 min-w-15rem" onClick={() => setShowPayerGroupingModal(true)} disabled={!selectedAccountNumber}>
          <span className="setup-tile-head flex justify-content-between align-items-center w-full text-selected"><span className="setup-tile-title text-sm font-semibold text-default leading-snug">Payer groupings</span><SetupChevron /></span>
          <span className="setup-tile-desc text-xs text-tertiary">Assign insurance payers to master payer groups used in scenario modeling.</span>
          <span className="setup-tile-summary text-xs text-secondary">{isLoadingPayerGroupings && !originalPayerRows.length ? 'Loading payer groupings…' : payerGroupingError && !originalPayerRows.length ? 'Payer groupings could not load' : `${plural(setupSummary.payers, 'payer')} · ${plural(setupSummary.groups, 'master group')}`}</span>
          {originalPayerRows.length > 0 && (setupSummary.unmapped > 0
            ? <span className="setup-tile-status u-mt-auto flex align-items-center u-gap-2 text-xs font-medium text-warning"><i aria-hidden="true" />{plural(setupSummary.unmapped, 'payer')} unmapped</span>
            : <span className="setup-tile-status u-mt-auto flex align-items-center u-gap-2 text-xs font-medium text-success"><i aria-hidden="true" />All payers mapped</span>)}
        </button>
        <button type="button" className="setup-tile flex-1 min-w-15rem" onClick={() => setShowFeeScheduleModal(true)} disabled={!selectedAccountNumber}>
          <span className="setup-tile-head flex justify-content-between align-items-center w-full text-selected"><span className="setup-tile-title text-sm font-semibold text-default leading-snug">Fee schedules</span><SetupChevron /></span>
          <span className="setup-tile-desc text-xs text-tertiary">Set reimbursement rates by product and master payer group.</span>
          <span className="setup-tile-summary text-xs text-secondary">{!coverageLoaded ? (isLoadingPayerGroupings || isLoadingCoverage ? 'Loading fee schedules…' : 'Fee-schedule coverage unavailable') : `${setupSummary.withRates} of ${plural(setupSummary.rateGroups, 'group')} with custom rates`}</span>
          {coverageLoaded && (setupSummary.withRates < setupSummary.rateGroups
            ? <span className="setup-tile-status u-mt-auto flex align-items-center u-gap-2 text-xs font-medium text-tertiary"><i aria-hidden="true" />{plural(setupSummary.rateGroups - setupSummary.withRates, 'group')} {setupSummary.rateGroups - setupSummary.withRates === 1 ? 'uses' : 'use'} the Medicare rate</span>
            : <span className="setup-tile-status u-mt-auto flex align-items-center u-gap-2 text-xs font-medium text-success"><i aria-hidden="true" />Every group has custom rates</span>)}
        </button>
      </div>

      <Dialog
        header={<ModalHeading title="Payer groupings" subtitle="Every insurance payer must belong to one master payer group. Groups drive reimbursement in scenarios." />}
        visible={showPayerGroupingModal}
        style={{ width: '80vw', maxWidth: '1200px', height: '80vh' }}
        contentStyle={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        className="payer-grouping-dialog"
        contentClassName="u-pt-0 flex-auto min-h-0"
        modal
        onHide={handlePayerGroupingModalHide}
        footer={activeTab === 2 && !uploadActions ? (
          <div className="setup-modal-footer flex align-items-center justify-content-end w-full"><Button variant="primary-outline" onClick={handlePayerGroupingModalHide}>Close</Button></div>
        ) :
          <div className="setup-modal-footer flex align-items-center u-gap-2 w-full">
            {activeTab !== 2 ? (
              // The tooltip hangs off this span, not off the Button via
              // tooltipOptions.showOnDisabled. showOnDisabled makes PrimeReact
              // re-parent the button into a raw wrapper div outside React, and this
              // button unmounts on the tab switch below -- React then removes it from
              // the wrong parent and the app dies with a removeChild NotFoundError.
              <>
                <span className="setup-footer-msg text-xs text-secondary">{tabChangeCount > 0 ? plural(tabChangeCount, 'unsaved change') : 'No unsaved changes'}</span>
                <span className="flex-1" />
                {activeTabHasChanges
                  ? <Button variant="primary-outline" onClick={confirmDiscardTabChanges} disabled={isSaving}>Discard</Button>
                  : <Button variant="primary-outline" onClick={handlePayerGroupingModalHide}>Cancel</Button>}
                <Tooltip target=".demo-tip-groupings-save" position="top" />
                <span
                  className={`inline-flex${isDemoMode ? ' demo-tip-groupings-save' : ''}`}
                  data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
                >
                  <Button
                    loading={isSaving}
                    onClick={handleSave}
                    // Demo accounts are read-only server-side (423).
                    disabled={isLoadingPayerGroupings || isSaving || !activeTabHasChanges || isDemoMode}
                  ><i className="pi pi-save" aria-hidden="true" />Save changes</Button>
                </span>
              </>
            ) : uploadActions ? (
              <div className="flex justify-content-between align-items-center w-full">
                <Button variant="secondary-text" onClick={uploadActions.onUploadDifferent}><i className="pi pi-upload" aria-hidden="true" />Upload different file</Button>
                <div className="flex u-gap-2">
                  <Button variant="primary-outline" onClick={() =>
                      confirm({
                        message: 'Discard the uploaded file and all pending changes?',
                        header: 'Discard upload',
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: 'Discard',
                        rejectLabel: 'Cancel',
                        danger: true,
                        accept: uploadActions.onDiscard,
                      })
                    }>Discard</Button>
                  <Button onClick={uploadActions.onApply} disabled={!uploadActions.canApply}><i className="pi pi-check" aria-hidden="true" />Apply changes</Button>
                </div>
              </div>
            ) : null}
          </div>
        }
      >
        {isLoadingPayerGroupings && (
          <div className="payer-grouping-status flex align-items-center justify-content-center u-gap-3 text-secondary u-py-6 u-px-0">
            <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="6" />
          </div>
        )}

        {!isLoadingPayerGroupings && payerGroupingError && (
          <div className="payer-grouping-status flex align-items-center justify-content-center u-gap-3 text-secondary u-py-6 u-px-0 text-danger">
            {payerGroupingError}
          </div>
        )}

        {!isLoadingPayerGroupings && !payerGroupingError && (
          <TabView className="flex flex-column flex-1 min-h-0" panelContainerClassName="flex-1 min-h-0 overflow-hidden flex flex-column" activeIndex={activeTab} onTabChange={(e) => handleTabChange(e.index)}>
            <TabPanel contentClassName="flex-1 min-h-0 flex flex-column" header={<span className="inline-flex align-items-center u-gap-2">Payer assignments{draftUnmappedCount > 0 && <StatusTag variant="warning">{draftUnmappedCount}</StatusTag>}</span>}>
              <div className="siq-dialog-scroll payer-grouping-modal-body">
                {plansLoaded ? (
                  <>
                    <p className="setup-tab-intro u-mt-0 u-mx-0 u-mb-3 text-xs text-tertiary">Choose a master payer group for each payer.</p>
                    <div className="flex u-gap-3 flex-wrap align-items-center u-mb-3">
                      <InputSearch
                        className="flex-1 min-w-13rem"
                        leftIcon={<i className="pi pi-search" aria-hidden="true" />}
                        value={payerSearch}
                        onChange={(e) => setPayerSearch(e.target.value)}
                        onClear={() => setPayerSearch('')}
                        clearButtonAriaLabel="Clear payer search"
                        placeholder="Search payers"
                        aria-label="Search payers"
                      />
                      <SegmentedControl
                        aria-label="Show payers"
                        className="w-auto"
                        options={[
                          { label: `All payers · ${payerRows.length}`, value: 'all' },
                          { label: `Unmapped · ${draftUnmappedCount}`, value: 'unmapped' },
                        ]}
                        value={payerFilter}
                        onValueChange={(value) => setPayerFilter(value === 'unmapped' ? 'unmapped' : 'all')}
                      />
                    </div>
                    <AppTable
                      isBordered={false}
                      size="sm"
                      className="siq-dense-inputs"
                      ariaLabel="Payer assignments"
                      table={payerTable}
                      rowClassName={({ rowData }) => (originalAssignment.get(rowData.insurancePayerPrimary) !== rowData.assignedMasterPayorGroup ? 'payer-row is-changed' : 'payer-row')}
                      messages={{ emptyStateText: 'No payers match these filters.' }}
                    />
                  </>
                ) : (
                  <div className="payer-grouping-status flex align-items-center justify-content-center u-gap-3 text-secondary u-py-6 u-px-0">No payer groupings found for this account.</div>
                )}
              </div>
            </TabPanel>

            <TabPanel contentClassName="flex-1 min-h-0 flex flex-column" header="Master payer groups" disabled={!plansLoaded}>
              <div className="siq-dialog-scroll master-payor-modal-body">
                <Tooltip position="top" target=".master-payor-name" />
                <Tooltip position="top" target=".master-payor-fs" />
                <div className="master-payor-add-row flex u-gap-2 align-items-center u-mb-3">
                  <InputText
                    value={newMasterPayorName}
                    onChange={(e) => setNewMasterPayorName(e.target.value)}
                    placeholder="New master payer group name"
                    aria-label="New master payer group name"
                    className="w-full"
                    maxLength={50}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddMasterPayor(); }}
                  />
                  <Button className="flex-none white-space-nowrap" onClick={handleAddMasterPayor} disabled={!newMasterPayorName.trim()}><i className="pi pi-plus" aria-hidden="true" />Add group</Button>
                </div>

                {masterPayorGroups.length === 0 && (
                  <div className="payer-grouping-status flex align-items-center justify-content-center u-gap-3 text-secondary u-py-6 u-px-0">No master payers defined.</div>
                )}

                {masterPayorGroups.length > 0 && (
                  <AppTable
                    isBordered={false}
                    size="sm"
                    className="siq-dense-inputs"
                    ariaLabel="Master payer groups"
                    table={masterTable}
                    rowClassName={({ rowData }) => [rowData.count === 0 ? 'master-payor-row--empty' : '', rowData.change ? 'is-changed' : ''].filter(Boolean).join(' ')}
                  />
                )}
                <p className="setup-note u-mt-3 u-mx-0 u-mb-0 text-xs text-tertiary">Deleting a group moves its payers to Other/Unmapped.</p>
              </div>
            </TabPanel>

            <TabPanel contentClassName="flex-1 min-h-0 flex flex-column" header="Bulk upload" disabled={!plansLoaded}>
              <PayerGroupingUpload
                existingPayers={payerRows.map((r) => ({
                  insurancePayerPrimary: r.insurancePayerPrimary,
                  assignedMasterPayorGroup: r.assignedMasterPayorGroup,
                }))}
                existingGroups={masterPayorGroups}
                onUploadStateChange={(actions) => setUploadActions(actions)}
                onApply={(uploadedRows, uploadedGroups) => {
                  const newRows: EditablePayerRow[] = uploadedRows.map((r, idx) => ({
                    rowId: r.assignedMasterPayorGroup + '::' + String(idx) + '::' + r.insurancePayerPrimary,
                    insurancePayerPrimary: r.insurancePayerPrimary,
                    assignedMasterPayorGroup: r.assignedMasterPayorGroup,
                  }));
                  setPayerRows(newRows);
                  setMasterPayorGroups(uploadedGroups);
                  // A bulk upload replaces the whole set — rename lineage can't be
                  // tracked through it, so any removed group is reconciled on save.
                  setGroupOrigin({});
                  setActiveTab(0);
                }}
              />
            </TabPanel>
          </TabView>
        )}
      </Dialog>
      <FeeScheduleModal
        visible={showFeeScheduleModal}
        accountNumber={selectedAccountNumber}
        masterPayorGroups={masterPayorGroups}
        onHide={() => setShowFeeScheduleModal(false)}
      />

      {/* Reconciliation prompt — shown on save when a group rename/removal affects fee schedules */}
      <Dialog
        header="Keep fee schedules in sync"
        visible={showReconcileDialog}
        style={{ width: '90vw', maxWidth: '640px' }}
        className="reconcile-dialog"
        modal
        onHide={() => {
          setShowReconcileDialog(false);
          setReconcilePlan(null);
        }}
        footer={
          <div className="flex justify-content-end u-gap-2" style={{ paddingTop: '0.4375rem' }}>
            <Button variant="primary-outline" onClick={() => {
                setShowReconcileDialog(false);
                setReconcilePlan(null);
              }} disabled={isSaving}>Cancel</Button>
            <Button
              loading={isSaving}
              onClick={applyReconciliation}
              disabled={isSaving || !reconcileValid}
            ><i className="pi pi-check" aria-hidden="true" />Apply &amp; Save</Button>
          </div>
        }
      >
        {reconcilePlan && (
          <div className="reconcile-body flex flex-column u-gap-4 overflow-y-auto u-pr-1">
            <p className="reconcile-intro u-m-0 text-xs text-secondary leading-normal">
              Fee-schedule rates are linked to payer groups by name. Your changes affect groups that
              have rates — choose what happens so no rate is silently lost.
            </p>

            {reconcilePlan.renames.length > 0 && (
              <div className="reconcile-section border-1 border-default radius-md u-p-3 bg-surface-container">
                <div className="reconcile-section-title flex align-items-center u-gap-1 text-xs font-bold text-secondary u-mb-2">
                  <i className="pi pi-arrow-right-arrow-left" /> Renamed — rates move automatically
                </div>
                {reconcilePlan.renames.map((r) => (
                  <div className="flex align-items-center u-gap-2 u-py-1 u-px-0 text-xs flex-wrap" key={`${r.from}->${r.to}`}>
                    <span className="reconcile-old font-semibold text-warning">{r.from}</span>
                    <i className="pi pi-arrow-right text-tertiary text-2xs" />
                    <span className="reconcile-new font-semibold text-selected">{r.to}</span>
                    <span className="reconcile-count u-ml-auto text-2xs text-tertiary white-space-nowrap">
                      {r.familyCount} product famil{r.familyCount === 1 ? 'y' : 'ies'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {reconcilePlan.removed.length > 0 && (
              <div className="reconcile-section border-1 border-default radius-md u-p-3 bg-surface-container">
                <div className="reconcile-section-title flex align-items-center u-gap-1 text-xs font-bold text-secondary u-mb-2">
                  <i className="pi pi-exclamation-triangle" /> Removed — these groups still have rates
                </div>
                {reconcilePlan.removed.map((d) => (
                  <div className="reconcile-removed-row" key={d.group}>
                    <div className="flex align-items-center u-gap-2 u-mb-1">
                      <span className="reconcile-old font-semibold text-warning">{d.group}</span>
                      <span className="reconcile-count u-ml-auto text-2xs text-tertiary white-space-nowrap">
                        {d.familyCount} product famil{d.familyCount === 1 ? 'y' : 'ies'}
                      </span>
                    </div>
                    <div className="flex flex-column u-gap-1">
                      <SegmentedControl
                        aria-label={`What happens to ${d.group}'s rates`}
                        className="w-auto"
                        options={[{ value: 'reassign', label: 'Reassign rates' }, { value: 'drop', label: 'Delete rates' }]}
                        value={d.action}
                        onValueChange={(next) => updateRemovedDecision(d.group, { action: next === 'drop' ? 'drop' : 'reassign' })}
                      />
                      {d.action === 'reassign' && (
                        <Dropdown
                          value={d.target ?? null}
                          options={masterPayorGroups
                            .slice()
                            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                            .map((g) => ({ label: g, value: g }))}
                          onChange={(e) => updateRemovedDecision(d.group, { target: String(e.value || '') })}
                          placeholder="Move rates to…"
                          className="reconcile-target max-w-18rem w-full"
                          appendTo={document.body}
                        />
                      )}
                      <p className="reconcile-choice-hint u-m-0 text-2xs text-tertiary leading-snug">
                        {d.action === 'reassign'
                          ? 'Rates move to the selected group.'
                          : 'Rates are permanently removed.'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AccountSettings;
